import { useEffect, useState } from 'react';
import { BellIcon, CalendarCheckIcon, SmartphoneIcon } from 'lucide-react';
import { CopyButton } from '../marketing/CopyButton';
import { fetchAgendaFeed } from '../adminApi';
import { btnGhost } from '../ui/styles';
import { REMINDER_HOURS } from '../../core/rules/agenda';
import { policy } from '../../data/policies';

/**
 * Koppelen met de agenda op haar telefoon (alleen lezen) en uitleg over wat de klant krijgt.
 * Bewust één richting: de salon-agenda is de waarheid. Twee kanten op synchroniseren met
 * Google geeft dubbele en spookafspraken; een privé-afspraak blokkeer je hier.
 */
export function CalendarSync({
  onClose
}: {
  onClose: () => void;
}) {
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let alive = true;
    fetchAgendaFeed().then(f => alive && setUrl(f.url)).catch(() => alive && setFailed(true));
    return () => {
      alive = false;
    };
  }, []);
  return <div className="space-y-6" data-id="element-361" data-mp="20jit52q8nndv">
      <div data-id="element-362" data-mp="20hpp7v70fh9k">
        <h2 className="font-display text-[1.4rem] font-light text-ink" data-id="element-363" data-mp="20i7qj68j4loc">Agenda op je telefoon</h2>
        <p className="mt-0.5 text-[13px] text-muted" data-id="element-364" data-mp="1wi7e1r4zqkjx">Je afspraken verschijnen vanzelf in Google Agenda of Apple Agenda. Wijzigen doe je hier.</p>
      </div>

      <section className="space-y-2" data-id="element-365" data-mp="1ms486qcrtc46">
        <h3 className="flex items-center gap-2 text-[14px] font-medium text-ink" data-id="element-366" data-mp="1mr2wcjnxklle">
          <SmartphoneIcon className="h-4 w-4 text-clay" strokeWidth={1.75} aria-hidden="true" data-id="element-367" data-mp="1htax14kyhx76" />
          Jouw agendalink
        </h3>
        {url ? <div className="flex items-center gap-2 rounded-md bg-sand px-3 py-2" data-id="element-368" data-mp="18rtllzihujot">
            <code className="min-w-0 flex-1 truncate text-[12px] text-ink" data-id="element-369" data-mp="18txnkbwu18mg">{url}</code>
            <CopyButton text={url} data-id="element-370" data-mp="13ukxzjektng6" />
          </div> : <p className="text-[13px] text-muted" role="status" data-id="element-371" data-mp="334t6o85n863h">
            {failed ? 'De link ophalen lukte niet. Probeer het zo opnieuw.' : 'Link ophalen…'}
          </p>}
        <ol className="list-decimal space-y-1 pl-5 text-[13px] leading-relaxed text-muted" data-id="element-372" data-mp="3haur7zli6v6e">
          <li data-id="element-373" data-mp="3m2mkwizyhh0r">
            <span className="text-ink" data-id="element-374" data-mp="3m2crelfpcrqb">iPhone:</span> Instellingen › Agenda › Accounts › Voeg account toe › Andere › Voeg agenda-abonnement toe, en plak de link.
          </li>
          <li data-id="element-375" data-mp="3d9jaykoirmav">
            <span className="text-ink" data-id="element-376" data-mp="3d8zs91r1otvs">Google Agenda</span> (op de computer): Andere agenda’s › + › Via URL, en plak de link.
          </li>
        </ol>
        <p className="text-[12px] leading-relaxed text-muted" data-id="element-377" data-mp="4rv76kvg26mk">
          Deel deze link met niemand: wie hem heeft, ziet je afspraken. In de agenda staan alleen voornaam en behandeling, geen telefoonnummers of notities. Komt er
          later iemand bij in de salon, dan krijgt ieder een eigen link.
        </p>
      </section>

      <section className="space-y-2" data-id="element-378" data-mp="2fmak0a5r1l1h">
        <h3 className="flex items-center gap-2 text-[14px] font-medium text-ink" data-id="element-379" data-mp="2fl9863gwsuip">
          <BellIcon className="h-4 w-4 text-clay" strokeWidth={1.75} aria-hidden="true" data-id="element-380" data-mp="2kldnhq62zj7c" />
          Wat de klant krijgt
        </h3>
        <ul className="space-y-2 text-[13px] leading-relaxed text-muted" data-id="element-381" data-mp="2d01imxdnkz66">
          <li className="flex gap-2" data-id="element-382" data-mp="281gggseuc5f3">
            <CalendarCheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-sage" strokeWidth={1.75} aria-hidden="true" data-id="element-383" data-mp="284m380zzyv7n" />
            <span data-id="element-384" data-mp="2h36h8oum4gpj">
              <span className="text-ink" data-id="element-385" data-mp="2h2odz89j9284">Bij bevestigen:</span> een mail met een agenda-uitnodiging. Verzet je de afspraak, dan schuift die in haar agenda mee.
            </span>
          </li>
          <li className="flex gap-2" data-id="element-386" data-mp="1pca55qgdmg3b">
            <BellIcon className="mt-0.5 h-4 w-4 shrink-0 text-sage" strokeWidth={1.75} aria-hidden="true" data-id="element-387" data-mp="1pdbdvo9x6qhv" />
            <span data-id="element-388" data-mp="234zp7pcchaxs">
              <span className="text-ink" data-id="element-389" data-mp="235hx5j8dddlj">{REMINDER_HOURS} uur vooraf:</span> een herinnering met adres, parkeren en de link om te verzetten. Kosteloos verzetten kan tot{' '}
              {policy.cancelHours} uur van tevoren. Geboekt binnen een dag? Dan geen herinnering; de bevestiging is dan vers genoeg.
            </span>
          </li>
        </ul>
        <p className="text-[12px] leading-relaxed text-muted" data-id="element-390" data-mp="1jbjb2mowiin6">Mails gaan uit zodra de maildienst gekoppeld is. Een sms of WhatsApp-herinnering kan later als extra.</p>
      </section>

      <button type="button" onClick={onClose} className={btnGhost} data-id="element-391" data-mp="1t8e6uxtsi4xr">
        Sluiten
      </button>
    </div>;
}